import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import type { FeatureFlag } from 'js/src/client';
import { ApiService, Urls } from 'js/src/client';
import { PrimaryButton } from 'js/src/components/common/ui/Button';
import Modal from 'js/src/components/common/ui/modal';
import Spinner from 'js/src/components/common/ui/Spinner';
import SwitchToggle from 'js/src/components/common/ui/SwitchToggle';
import { hideModal, isFeatureFlagsManagementModalShowing } from 'js/src/data/modals';
import { useAppDispatch } from 'js/src/libs/RootState';

const FeatureFlagsModal: React.FC = () => {
    const isShowing = useSelector(isFeatureFlagsManagementModalShowing);
    const dispatch = useAppDispatch();
    const [flags, setFlags] = useState<FeatureFlag[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [togglingFlags, setTogglingFlags] = useState<Set<string>>(new Set());

    const fetchFlags = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await ApiService.flagListAllList();
            setFlags(response.data);
        } catch (e) {
            setError('Failed to load feature flags');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isShowing) {
            void fetchFlags();
        }
    }, [isShowing, fetchFlags]);

    const handleToggle = useCallback(async (flagName: string, isCurrentlyEnrolled: boolean) => {
        setTogglingFlags((prev) => new Set(prev).add(flagName));
        try {
            if (isCurrentlyEnrolled) {
                await ApiService.flagWithdrawRetrieve({ path: { id: flagName } });
            } else {
                await ApiService.flagEnrollRetrieve({ path: { id: flagName } });
            }
            setFlags((prev) =>
                prev.map((flag) =>
                    flag.name === flagName ? { ...flag, is_enrolled: !isCurrentlyEnrolled } : flag,
                ),
            );
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Failed to toggle feature flag:', e);
        } finally {
            setTogglingFlags((prev) => {
                const next = new Set(prev);
                next.delete(flagName);
                return next;
            });
        }
    }, []);

    const onDone = useCallback(() => {
        dispatch(hideModal());
        window.location.reload();
    }, [dispatch]);

    const onClose = useCallback(() => {
        dispatch(hideModal());
    }, [dispatch]);

    const handleSwitchChange = useCallback(
        (flagName: string, isCurrentlyEnrolled: boolean) => () => {
            void handleToggle(flagName, isCurrentlyEnrolled);
        },
        [handleToggle],
    );

    return (
        <Modal showing={isShowing} title="Feature flags [staff only]" onHidden={onClose}>
            <div>
                {loading && (
                    <div className="d-flex justify-content-center py-l5-32">
                        <Spinner />
                    </div>
                )}
                {error && <div className="text-danger">{error}</div>}
                {!loading && !error && flags.length === 0 && (
                    <div className="text-muted">No active feature flags found.</div>
                )}
                {!loading && !error && flags.length > 0 && (
                    <div className="d-flex flex-column gap-3">
                        {flags.map((flag) => (
                            <div
                                key={flag.name}
                                className="d-flex justify-content-between align-items-center"
                            >
                                <span className="font-monospace">{flag.name}</span>
                                <SwitchToggle
                                    checked={flag.is_enrolled}
                                    disabled={togglingFlags.has(flag.name)}
                                    onChange={handleSwitchChange(flag.name, flag.is_enrolled)}
                                />
                            </div>
                        ))}
                    </div>
                )}
                <p className="font-l5 font-l5--extra-small mt-l5-16 mb-l5-0">
                    Available flags can be managed{' '}
                    <a href={Urls.FEATURE_FLAGS_CHANGELIST} target="_blank">
                        here
                    </a>
                </p>
            </div>
            <div className="modal-button-footer">
                <PrimaryButton text="Done" onClick={onDone} />
            </div>
        </Modal>
    );
};

export default FeatureFlagsModal;
